import React from "react";
import Header from "../components/header/header";
import Footer from "../components/footer/footer";
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import "./privacy.scss";

const EFFECTIVE_DATE = "October 20, 2026";

const Privacy = () => {
    return (
        <div className="privacy-page">
            <div className="privacy-page-header">
                <Header />
            </div>

            <div className="privacy-page-container">
                <h1 className="privacy-title">
                    <ShieldOutlinedIcon />&nbsp;&nbsp;Privacy Policy
                </h1>
                <p className="privacy-effective">
                    <strong>Effective date:</strong> {EFFECTIVE_DATE}
                </p>

                <section className="privacy-section">
                    <p>
                        This Privacy Policy explains how VirtualPsych
                        (&ldquo;VirtualPsych,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
                        &ldquo;our&rdquo;) collects, uses, discloses, and protects information
                        when you visit our website, contact us, request or receive care, or
                        communicate with us by phone, email, or text message. By using our
                        website or services, you agree to the practices described in this
                        Privacy Policy.
                    </p>
                    <p>
                        Protected health information that we create or receive in connection
                        with providing care is also governed by the Health Insurance
                        Portability and Accountability Act (HIPAA) and our Notice of Privacy
                        Practices, which controls over this Privacy Policy where it applies.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>1. Information We Collect</h2>
                    <p>We collect the following categories of information:</p>
                    <ul>
                        <li>
                            <strong>Information you provide to us.</strong> Name, date of
                            birth, mailing address, email address, mobile phone number,
                            insurance information, reason for visit, appointment details,
                            payment information, and any other information you provide through
                            forms, our scheduling tools, phone calls, emails, or text
                            messages.
                        </li>
                        <li>
                            <strong>Health information.</strong> Information you share with
                            your provider during a visit, including symptoms, history,
                            diagnoses, and treatment. This information is handled as protected
                            health information under HIPAA.
                        </li>
                        <li>
                            <strong>Mobile phone number and SMS consent records.</strong>
                            When you give us your mobile phone number, we collect that number
                            and record your consent to receive text messages, including the
                            date, time, and method of consent.
                        </li>
                        <li>
                            <strong>Automatically collected information.</strong> When you
                            visit our website, we may collect your IP address, browser type,
                            device identifiers, pages viewed, referring URL, and similar usage
                            data through cookies and similar technologies.
                        </li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>2. How We Use Information</h2>
                    <p>We use the information we collect to:</p>
                    <ul>
                        <li>Provide, schedule, and deliver clinical care and related services;</li>
                        <li>
                            Send appointment confirmations, reminders, rescheduling messages,
                            and replies to your questions about your visit;
                        </li>
                        <li>Process payments and handle insurance billing;</li>
                        <li>Operate, secure, and improve our website and services;</li>
                        <li>Comply with legal, regulatory, and professional obligations; and</li>
                        <li>Respond to your inquiries and support requests.</li>
                    </ul>
                    <p>
                        We do not use your mobile phone number or SMS communications to send
                        you marketing or promotional messages.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>3. SMS / Text Messaging Program</h2>

                    <h4>Information we collect.</h4>
                    <p>
                        When you give VirtualPsych your mobile phone number, we collect that
                        number and record your consent to receive text messages, including the
                        date and how you consented.
                    </p>

                    <h4>How we use it.</h4>
                    <p>
                        We use your mobile number only to send messages about your care with
                        us: appointment confirmations, reminders, rescheduling, and replies to
                        your questions about your visit. We do not send marketing messages.
                    </p>

                    <h4>Opting out.</h4>
                    <p>
                        You can stop text messages at any time by replying <strong>STOP</strong>.
                        Reply <strong>HELP</strong> for help, or call us at{" "}
                        <a href="tel:+18778394824" className="privacy-phone">(877) 839-4824</a>.
                        Message frequency varies. Message and data rates may apply. Carriers
                        are not liable for delayed or undelivered messages.
                    </p>

                    <h4>Supported carriers.</h4>
                    <p>
                        Our SMS program is compatible with major U.S. carriers, including
                        AT&amp;T, T-Mobile, Verizon Wireless, Sprint, Boost, U.S. Cellular,
                        MetroPCS, and others.
                    </p>
                </section>

                <section className="privacy-section privacy-callout">
                    <h2>4. No Sharing or Selling of SMS Opt-In Data</h2>
                    <p>
                        <strong>
                            No mobile information will be shared with third parties or
                            affiliates for marketing or promotional purposes.
                        </strong>{" "}
                        All categories of information described in this Privacy Policy exclude
                        text messaging originator opt-in data and consent; this information
                        <strong> will not be shared with or sold to any third parties</strong>{" "}
                        under any circumstances.
                    </p>
                    <p>
                        Mobile phone numbers and SMS consent records may be disclosed only to
                        service providers (such as our messaging and telecommunications
                        providers) that help us deliver messages on our behalf, and only to
                        the extent necessary to deliver those messages. These service
                        providers are contractually prohibited from using the information for
                        their own marketing or from selling or sharing it with any other
                        party.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>5. How We Share Other Information</h2>
                    <p>
                        Outside of SMS opt-in data (which is governed by Section 4 above), we
                        share information only as described below:
                    </p>
                    <ul>
                        <li>
                            <strong>With your providers and care team</strong> to deliver and
                            coordinate your care.
                        </li>
                        <li>
                            <strong>With service providers</strong> (such as hosting,
                            scheduling, payment processing, analytics, and communications
                            vendors) that perform services on our behalf under written
                            agreements requiring them to protect your information and use it
                            only for the services we have engaged them to provide.
                        </li>
                        <li>
                            <strong>With insurers and payers</strong> as needed to verify
                            benefits, obtain authorization, and process claims.
                        </li>
                        <li>
                            <strong>For legal and safety reasons</strong>, when required by
                            law, subpoena, or court order, or to protect the rights, property,
                            or safety of our patients, our staff, or the public.
                        </li>
                        <li>
                            <strong>With your authorization</strong>, including any
                            disclosures that require your written authorization under HIPAA.
                        </li>
                    </ul>
                    <p>
                        <strong>
                            We do not sell your personal information, and we do not share your
                            personal information with third parties for their own marketing or
                            promotional purposes.
                        </strong>
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>6. Cookies and Analytics</h2>
                    <p>
                        Our website uses cookies and similar technologies to operate the site,
                        remember preferences, and understand how visitors use our pages. You
                        can control cookies through your browser settings. Disabling cookies
                        may affect the functionality of some parts of our website.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>7. Data Retention</h2>
                    <p>
                        We retain information for as long as needed to provide our services,
                        meet our legal, tax, accounting, and regulatory obligations, and
                        resolve disputes. Medical records are retained for the periods
                        required by applicable law. SMS consent records are retained for as
                        long as required to document your consent and for a reasonable period
                        after you opt out, after which they are deleted or de-identified.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>8. Data Security</h2>
                    <p>
                        We use administrative, technical, and physical safeguards designed to
                        protect your information, including encryption in transit, access
                        controls, and workforce training. No method of transmission or storage
                        is 100% secure, and we cannot guarantee absolute security.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>9. Your Choices and Rights</h2>
                    <ul>
                        <li>
                            <strong>SMS.</strong> Reply <strong>STOP</strong> to any text
                            message to opt out, or <strong>HELP</strong> for help.
                        </li>
                        <li>
                            <strong>Email.</strong> You may contact us to update or correct
                            your contact information at any time.
                        </li>
                        <li>
                            <strong>Health information rights.</strong> Patients have rights
                            under HIPAA regarding their protected health information,
                            including the right to access and request amendments to their
                            records. See our Notice of Privacy Practices for details.
                        </li>
                        <li>
                            <strong>State privacy rights.</strong> Depending on where you
                            live, you may have additional rights (such as the right to
                            request access to, correction of, or deletion of certain personal
                            information). Contact us using the information below to exercise
                            those rights.
                        </li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>10. Children&rsquo;s Privacy</h2>
                    <p>
                        Our website is not directed to children under 13, and we do not
                        knowingly collect personal information from children under 13 through
                        our website. Clinical services for minors are provided only through
                        the involvement of a parent or legal guardian in accordance with
                        applicable law.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>11. Third-Party Links</h2>
                    <p>
                        Our website may contain links to third-party sites. We are not
                        responsible for the privacy practices or content of those sites. We
                        encourage you to review the privacy policies of any third-party site
                        you visit.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>12. Changes to This Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time. When we do, we
                        will update the &ldquo;Effective date&rdquo; above and post the
                        revised policy on this page. Your continued use of our website or
                        services after any change takes effect means you accept the updated
                        policy.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>13. Contact Us</h2>
                    <p>
                        If you have questions about this Privacy Policy or our privacy
                        practices, please contact us at:
                    </p>
                    <p>
                        <strong>VirtualPsych</strong>
                        <br />
                        1715 Indian Wood Circle, Maumee, OH 43537
                        <br />
                        Phone:{" "}
                        <a href="tel:+18778394824" className="privacy-phone">(877) 839-4824</a>
                        <br />
                        Email:{" "}
                        <a href="mailto:support@virtualpsych.com" className="privacy-phone">
                            support@virtualpsych.com
                        </a>
                    </p>
                </section>
            </div>

            <Footer />
        </div>
    );
};

export default Privacy;
